import { useEffect, useRef, useState } from 'react';
import { handleiding } from '../handleiding';

/**
 * De handleiding, over het scherm heen.
 *
 * WAAROM EEN LAAG EN GEEN EIGEN PAGINA. Je slaat dit op het moment open dat je
 * ergens middenin zit: een knop waarvan je niet weet wat hij doet, terwijl de
 * ander wacht. Een aparte pagina zou het gesprek verlaten en de verbinding
 * opnieuw laten opbouwen. Deze laag legt zich er alleen overheen; eronder
 * loopt alles door.
 */
export default function Handleiding({
  taalcode,
  opSluiten,
}: {
  taalcode: string;
  opSluiten: () => void;
}) {
  const h = handleiding(taalcode);
  const sluitRef = useRef<HTMLButtonElement>(null);

  /**
   * Het blok dat nu voorgelezen wordt, of null.
   *
   * EEN TEGELIJK, net als in de kamer. Twee stemmen door elkaar is
   * onverstaanbaar, en een tweede knop indrukken hoort de eerste te stoppen in
   * plaats van erover heen te gaan spelen.
   */
  const [klinkt, setKlinkt] = useState<string | null>(null);
  const spelerRef = useRef<HTMLAudioElement | null>(null);

  function speel(sleutel: string): void {
    spelerRef.current?.pause();
    if (klinkt === sleutel) {
      setKlinkt(null);
      return;
    }
    // De bestanden komen uit client/public/handleiding/ en zijn eenmalig
    // gemaakt met npm run handleiding:spraak. Ze staan in git; hier wordt
    // niets aangeroepen en dus niets betaald.
    const audio = new Audio(`/handleiding/${h.code}-${sleutel}.mp3`);
    spelerRef.current = audio;
    audio.onended = () => setKlinkt(null);
    // Ontbreekt het bestand, dan gaat de knop gewoon weer uit. Een melding
    // over een handleiding die niet klinkt, helpt niemand verder.
    audio.onerror = () => setKlinkt(null);
    setKlinkt(sleutel);
    void audio.play().catch(() => setKlinkt(null));
  }

  // Het venster sluiten stopt het geluid. Anders praat de handleiding door
  // terwijl je alweer in het gesprek zit.
  useEffect(() => {
    return () => {
      spelerRef.current?.pause();
      spelerRef.current = null;
    };
  }, []);

  // Escape sluit, en de aandacht springt naar de sluitknop. Wie met het
  // toetsenbord werkt, staat anders ergens achter de laag.
  useEffect(() => {
    sluitRef.current?.focus();
    const opToets = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') opSluiten();
    };
    document.addEventListener('keydown', opToets);
    return () => document.removeEventListener('keydown', opToets);
  }, [opSluiten]);

  return (
    <div
      className="fixed inset-0 z-50 bg-gio-dark/45 flex items-center justify-center p-3
                 animate-fade-in"
      onClick={opSluiten}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-label={h.titel}
        // Een klik BINNEN het venster mag niet doorlopen naar de achtergrond,
        // anders sluit het zichzelf zodra je iets aanwijst.
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-md max-h-full bg-white border-2 border-slate-250 rounded-2xl
                   shadow-xs flex flex-col overflow-hidden"
      >
        <div className="flex items-start justify-between gap-3 p-4 pb-3 border-b-2 border-slate-150
                        shrink-0">
          <span className="min-w-0">
            <h2 className="text-sm font-extrabold text-gio-dark leading-tight">{h.titel}</h2>
            <p className="mt-1 text-[11px] text-slate-550 leading-relaxed">{h.inleiding}</p>
          </span>
          <button
            ref={sluitRef}
            type="button"
            onClick={opSluiten}
            title={h.sluiten}
            aria-label={h.sluiten}
            className="w-7 h-7 shrink-0 rounded-lg border-2 border-slate-250 bg-slate-50
                       text-slate-550 text-sm font-bold flex items-center justify-center
                       hover:border-slate-350 transition-colors duration-155"
          >
            ×
          </button>
        </div>

        {/*
          MEEPRATEN BOVENAAN, BEHEER ERONDER, EN ALLES INKLAPBAAR.
          Wie een uitnodiging kreeg en de kamer binnenloopt, heeft niets aan
          toon, woordenlijsten of budgetten. Die moest er eerst doorheen
          scrollen voor hij bij zijn eigen vraag was. Nu staan de twee
          gebruiksblokken die hij het eerst nodig heeft al open, en de rest is
          een regel die hij kan overslaan.
        */}
        <div className="flex-1 min-h-0 overflow-y-auto p-4 pt-3">
          {(['gebruik', 'beheer'] as const).map((groep) => {
            const delen = h.delen.filter((d) => d.groep === groep);
            if (delen.length === 0) return null;
            return (
              <div key={groep} className="mb-3 last:mb-0">
                <h3
                  className="text-[10px] font-bold uppercase tracking-wider text-gio-orange-deep
                             mb-1.5"
                >
                  {groep === 'gebruik' ? h.groepGebruik : h.groepBeheer}
                </h3>

                {delen.map((deel) => (
                  <details
                    key={deel.titel}
                    open={deel.open === true}
                    className="group border-t border-slate-150 py-1.5 first:border-t-0 first:pt-0"
                  >
                    <summary
                      className="flex items-center gap-1.5 text-[11px] font-bold text-gio-blue
                                 hover:text-gio-dark cursor-pointer select-none list-none
                                 transition-colors duration-155"
                    >
                      <span className="flex-1">{deel.titel}</span>

                      {/*
                        Alleen bij de gebruiksblokken: daar is audio voor
                        gemaakt. In een SPAN en niet in een knop, want een knop
                        in een summary opent de details bij elke klik - je zou
                        het blok dus openklappen als je alleen wilt luisteren.
                      */}
                      {groep === 'gebruik' && (
                        <span
                          role="button"
                          tabIndex={0}
                          aria-label={deel.titel}
                          aria-pressed={klinkt === deel.sleutel}
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            speel(deel.sleutel);
                          }}
                          onKeyDown={(e) => {
                            if (e.key !== 'Enter' && e.key !== ' ') return;
                            e.preventDefault();
                            e.stopPropagation();
                            speel(deel.sleutel);
                          }}
                          className={`w-6 h-6 shrink-0 rounded-lg border-2 flex items-center
                                      justify-center transition-colors duration-155 ${
                                        klinkt === deel.sleutel
                                          ? 'border-gio-orange bg-gio-orange/15 text-gio-orange-deep'
                                          : 'border-slate-250 bg-white text-slate-550 hover:border-slate-350'
                                      }`}
                        >
                          <span className="text-[10px] leading-none">
                            {klinkt === deel.sleutel ? '■' : '▶'}
                          </span>
                        </span>
                      )}

                      <span className="font-mono text-slate-450 group-open:hidden">+</span>
                      <span className="font-mono text-slate-450 hidden group-open:inline">−</span>
                    </summary>

                    <dl className="space-y-1.5 mt-1.5">
                      {deel.punten.map((punt) => (
                        <div key={punt.kop} className="text-[11px] leading-relaxed">
                          <dt className="font-bold text-gio-dark inline">{punt.kop}. </dt>
                          <dd className="inline text-slate-650">{punt.uitleg}</dd>
                        </div>
                      ))}
                    </dl>
                  </details>
                ))}
              </div>
            );
          })}
        </div>

        <div className="shrink-0 p-3 border-t-2 border-slate-150">
          <button
            type="button"
            onClick={opSluiten}
            className="w-full py-2 rounded-xl bg-gio-blue hover:bg-gio-dark text-white
                       font-bold text-xs transition-colors duration-155"
          >
            {h.sluiten}
          </button>
        </div>
      </div>
    </div>
  );
}
